/**
 * User Service
 * Handles all user-related API calls
 *
 * @module services/UserService
 */

import BaseService from "core/api/BaseService";
import { multipartApiClient } from "core/api/apiClient";
import { API_ENDPOINTS } from "utils/constants";
import storage from "utils/storage";
import { handleResponse } from "utils/responseHandler";

class UserService extends BaseService {
  constructor() {
    super(API_ENDPOINTS.USERS.ME);
  }

  /**
   * Get current authenticated user
   * @returns {Promise} Current user data
   */
  async me() {
    return this.get(API_ENDPOINTS.USERS.ME);
  }

  /**
   * Update current user profile
   * @param {Object} data - Profile data to update
   * @returns {Promise} Updated user data
   */
  async updateProfile(data) {
    return this.put(API_ENDPOINTS.USERS.ME, data);
  }

  /**
   * Alias for updateProfile (used by SettingsPage)
   * @param {Object} data - Profile data to update
   * @returns {Promise} Updated user data
   */
  async updateMe(data) {
    return this.updateProfile(data);
  }

  /**
   * Upload avatar
   * @param {FormData} formData - Form data with avatar file
   * @returns {Promise} Upload result with avatar URL
   */
  async uploadAvatar(formData) {
    // Use multipartApiClient directly for correct Content-Type bounds
    const response = await multipartApiClient.post(API_ENDPOINTS.USERS.AVATAR, formData);

    // Update user avatar in storage
    if (response.data?.success && response.data?.data?.avatarUrl) {
        const currentUser = storage.auth.getUser();
        // Handle nested data structure if necessary
        const avatarUrl = response.data.data.avatarUrl;
        storage.auth.setUser({ ...currentUser, avatar: avatarUrl });
        return response.data; // Return the inner data directly to match BaseService pattern
    } else if (response.data?.success && response.data?.user) {
         // Alternative response structure check
         const currentUser = storage.auth.getUser();
         storage.auth.setUser({ ...currentUser, ...response.data.user });
         return response.data;
    }

    return response.data || response;
  }

  /**
   * Upload cover photo
   * @param {FormData} formData - Form data with cover photo file
   * @returns {Promise} Upload result with cover photo URL
   */
  async uploadCoverPhoto(formData) {
    const response = await multipartApiClient.post(API_ENDPOINTS.USERS.COVER, formData);

    if (response.data?.success && response.data?.user) {
         const currentUser = storage.auth.getUser();
         storage.auth.setUser({ ...currentUser, ...response.data.user });
         return response.data;
    }

    return response.data || response;
  }

  /**
   * Get user preferences
   * @returns {Promise} User preferences
   */
  async getPreferences() {
    return this.get(API_ENDPOINTS.USERS.PREFERENCES);
  }

  /**
   * Update user preferences
   * @param {Object} preferences - Updated preferences
   * @returns {Promise} Update result
   */
  async updatePreferences(preferences) {
    const response = await this.put(
      API_ENDPOINTS.USERS.PREFERENCES,
      preferences
    );

    // Update preferences in storage
    if (response.success && response.data) {
      storage.preferences.set(response.data);
    }

    return response;
  }

  /**
   * Get user notifications
   * @returns {Promise} User notifications
   */
  async getNotifications() {
    return this.get(API_ENDPOINTS.USERS.NOTIFICATIONS);
  }

  /**
   * Mark notification as read
   * @param {string} notificationId - Notification ID
   * @returns {Promise} Update result
   */
  async markNotificationRead(notificationId) {
    return this.put(`${API_ENDPOINTS.USERS.NOTIFICATIONS}/${notificationId}/read`, {});
  }

  /**
   * Mark all notifications as read
   * @returns {Promise} Update result
   */
  async markAllNotificationsRead() {
    return this.put(`${API_ENDPOINTS.USERS.NOTIFICATIONS}/read-all`, {});
  }

  /**
   * Delete notification
   * @param {string} notificationId - Notification ID
   * @returns {Promise} Delete result
   */
  async deleteNotification(notificationId) {
    return this.deleteCustom(`${API_ENDPOINTS.USERS.NOTIFICATIONS}/${notificationId}`);
  }

  /**
   * Get user by ID
   * @param {string} userId - User ID
   * @returns {Promise} User data
   */
  async getUserById(userId) {
    return handleResponse(this.client.get(`users/${userId}`));
  }

  /**
   * Update the authenticated user's own profile.
   * NOTE: the backend only exposes `PUT /users/me` for profile edits — there is
   * no `PUT /users/:id`. `userId` is accepted for call-site clarity but the
   * update always targets the current user (self). Admin user mutations go
   * through dedicated endpoints (promote/demote, /admin/users).
   * @param {string} [userId] - Ignored; kept for signature compatibility.
   * @param {Object} data - Profile data
   * @returns {Promise} Updated user data
   */
  // eslint-disable-next-line no-unused-vars
  async updateUser(userId, data) {
    return this.put(API_ENDPOINTS.USERS.ME, data);
  }

  /**
   * Change password
   * @param {Object} data - { currentPassword, newPassword }
   * @returns {Promise} Change result
   */
  async changePassword(data) {
    return this.put(API_ENDPOINTS.USERS.PASSWORD, data);
  }

  /**
   * Search users by name or email
   * @param {string} query - Search query
   * @returns {Promise} Search results
   */
  async searchUsers(query) {
    return handleResponse(this.client.get(`users/search?q=${encodeURIComponent(query)}`));
  }

  /**
   * Delete account
   * @returns {Promise} Delete result
   */
  async deleteAccount() {
    const response = await this.deleteCustom(API_ENDPOINTS.USERS.ME);

    // Clear storage on successful deletion
    if (response.success) {
      storage.auth.clearAuth();
    }

    return response;
  }
}

// Export singleton instance
export default new UserService();

